import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { supabase } from './supabaseClient';
import './AddFlight.css';

const AddPrivateJet = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    aircraft_type: '',
    manufacturer: '',
    model: '',
    from_location: '',
    to_location: '',
    price: '',
    capacity: '',
    max_range: '',
    cruise_speed: '',
    amenities: '',
    description: '',
    image_url: '',
    status: 'active',
    available_from: '',
    available_to: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.aircraft_type) {
      if (!formData.manufacturer) newErrors.manufacturer = 'Manufacturer is required';
      if (!formData.model) newErrors.model = 'Model is required';
    }
    if (!formData.from_location) newErrors.from_location = 'From location is required';
    if (!formData.to_location) newErrors.to_location = 'To location is required';
    if (!formData.price) newErrors.price = 'Price is required';
    if (!formData.capacity) newErrors.capacity = 'Capacity is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('private_jets')
        .insert([{
          ...formData,
          price: parseFloat(formData.price),
          capacity: parseInt(formData.capacity),
          max_range: formData.max_range ? parseFloat(formData.max_range) : null,
          cruise_speed: formData.cruise_speed ? parseFloat(formData.cruise_speed) : null,
          available_from: formData.available_from || null,
          available_to: formData.available_to || null
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      alert('Private jet added successfully!');
      navigate('/admin/private-jets');
    } catch (error) {
      console.error('Error adding private jet:', error);
      alert('Failed to add private jet. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/private-jets');
  };

  return (
    <AdminLayout pageTitle="Add Private Jet">
      <div className="admin-add-flight-page">
        {/* Page Title */}
        <div className="admin-add-flight-header">
          <h1 className="admin-add-flight-title">Add Private Jet</h1>
          <nav className="admin-breadcrumb">
            <button 
              className="admin-breadcrumb-link"
              onClick={() => navigate('/admin/private-jets')}
            >
              Private Jets
            </button>
            <span className="admin-breadcrumb-separator">→</span>
            <span className="admin-breadcrumb-current">Add Private Jet</span>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="admin-flight-form">
          {/* Section 1: Basic Details */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <h2 className="admin-form-section-title">Basic Details</h2>
            </div>
            <div className="admin-form-section-content">
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Name <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.name ? 'error' : ''}`}
                    placeholder="e.g., Luxury Gulfstream G650"
                  />
                  {errors.name && (
                    <span className="admin-form-error">{errors.name}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Aircraft Type <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="aircraft_type"
                    value={formData.aircraft_type}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.aircraft_type ? 'error' : ''}`}
                    placeholder="e.g., Gulfstream G650"
                  />
                  {errors.aircraft_type && (
                    <span className="admin-form-error">{errors.aircraft_type}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Manufacturer</label>
                  <input
                    type="text"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.manufacturer ? 'error' : ''}`}
                    placeholder="e.g., Gulfstream"
                  />
                  {errors.manufacturer && (
                    <span className="admin-form-error">{errors.manufacturer}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Model</label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.model ? 'error' : ''}`}
                    placeholder="e.g., G650"
                  />
                  {errors.model && (
                    <span className="admin-form-error">{errors.model}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="admin-form-select"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Route */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <h2 className="admin-form-section-title">Route</h2>
            </div>
            <div className="admin-form-section-content">
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    From Location <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="from_location"
                    value={formData.from_location}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.from_location ? 'error' : ''}`}
                    placeholder="e.g., Lagos, Nigeria"
                  />
                  {errors.from_location && (
                    <span className="admin-form-error">{errors.from_location}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    To Location <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="to_location"
                    value={formData.to_location}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.to_location ? 'error' : ''}`}
                    placeholder="e.g., London, UK"
                  />
                  {errors.to_location && (
                    <span className="admin-form-error">{errors.to_location}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Available From</label>
                  <input
                    type="date"
                    name="available_from"
                    value={formData.available_from}
                    onChange={handleInputChange}
                    className="admin-form-input"
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Available To</label>
                  <input
                    type="date"
                    name="available_to"
                    value={formData.available_to}
                    onChange={handleInputChange}
                    className="admin-form-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Specifications */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <h2 className="admin-form-section-title">Specifications</h2>
            </div>
            <div className="admin-form-section-content">
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Capacity (Passengers) <span className="admin-required">*</span>
                  </label>
                  <input
                    type="number"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.capacity ? 'error' : ''}`}
                    placeholder="e.g., 12"
                    min="1"
                  />
                  {errors.capacity && (
                    <span className="admin-form-error">{errors.capacity}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Max Range (miles)</label>
                  <input
                    type="number"
                    name="max_range"
                    value={formData.max_range}
                    onChange={handleInputChange}
                    className="admin-form-input"
                    placeholder="e.g., 7000"
                    min="0"
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Cruise Speed (mph)</label>
                  <input
                    type="number"
                    name="cruise_speed"
                    value={formData.cruise_speed}
                    onChange={handleInputChange}
                    className="admin-form-input"
                    placeholder="e.g., 516"
                    min="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: Pricing */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <h2 className="admin-form-section-title">Pricing</h2>
            </div>
            <div className="admin-form-section-content">
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Price (₦) <span className="admin-required">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.price ? 'error' : ''}`}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                  {errors.price && (
                    <span className="admin-form-error">{errors.price}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Additional Information */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <h2 className="admin-form-section-title">Additional Information</h2>
            </div>
            <div className="admin-form-section-content">
              <div className="admin-form-grid">
                <div className="admin-form-group admin-form-group-full">
                  <label className="admin-form-label">Amenities</label>
                  <textarea
                    name="amenities"
                    value={formData.amenities}
                    onChange={handleInputChange}
                    className="admin-form-textarea"
                    placeholder="e.g., Wi-Fi, Full Bar, Conference Room, Bedroom, etc."
                    rows="3"
                  />
                </div>

                <div className="admin-form-group admin-form-group-full">
                  <label className="admin-form-label">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="admin-form-textarea"
                    placeholder="Detailed description of the private jet..."
                    rows="5"
                  />
                </div>

                <div className="admin-form-group admin-form-group-full">
                  <label className="admin-form-label">Image URL</label>
                  <input
                    type="url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleInputChange}
                    className="admin-form-input"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-form-button admin-form-button-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-form-button admin-form-button-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Private Jet'}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AddPrivateJet;
